/**
 * <StudentGenerateActivityOverlay> — « Générer une activité » de l'élève, monté seulement quand il
 * est ouvert.
 *
 * La même enveloppe que celle du parent (`parent/Activities/GenerateActivityOverlay`) : le parcours
 * installe une trentaine de crochets et quatre tranches d'appel, et l'onglet reste monté ; il
 * n'existe donc que pendant l'ouverture, et rend ses tranches au repos en partant. Ce qui change est
 * le crochet, celui de l'élève (`useStudentGenerateActivity`).
 */
import React, { useCallback, useEffect, useRef } from "react"

import { GenerateActivityFlow } from "../../../../../../design-system"
import { useStudentGenerateActivity } from "./useStudentGenerateActivity"

export type StudentGenerateActivityOverlayProps = {
    studentId: string | number | null
    /** Rejoué après une création : la liste doit montrer la nouvelle activité. */
    onCreated?: () => void
    /** Faux ⇒ le parcours n'est pas monté du tout. */
    open: boolean
    onClose: () => void
}

export function StudentGenerateActivityOverlay(props: StudentGenerateActivityOverlayProps) {
    const { open, ...rest } = props
    if (!open) return null
    return <MountedStudentGenerateActivityOverlay {...rest} />
}

function MountedStudentGenerateActivityOverlay(props: Omit<StudentGenerateActivityOverlayProps, "open">) {
    const { studentId, onCreated, onClose } = props
    const flow = useStudentGenerateActivity({ studentId, onCreated })

    // Le montage EST l'ouverture ; la garde empêche un rechargement du compte (qui redéclare
    // `open`) de remettre le brouillon à zéro.
    const openFlow = flow.open
    const openedRef = useRef(false)
    useEffect(() => {
        if (openedRef.current) return
        openedRef.current = true
        openFlow()
    }, [openFlow])

    const close = flow.generate.onClose
    const handleClose = useCallback(() => {
        close?.()
        onClose()
    }, [close, onClose])

    return <GenerateActivityFlow {...flow.generate} onClose={handleClose} />
}
